import React, { useEffect, useState } from 'react'
import { api } from '../services/api'
import { Search, ChevronLeft, ChevronRight, Users, Plus, Edit, Shield, Trash2 } from 'lucide-react'
import FuncionarioModal from '../components/FuncionarioModal'
import PermissoesModal from '../components/PermissoesModal'
import { useAuth } from '../contexts/AuthContext'

const FuncionariosList = () => {
  const [funcionarios, setFuncionarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(20)
  const [total, setTotal] = useState(0)
  const [searchTimeout, setSearchTimeout] = useState(null)
  const [ativoFilter, setAtivoFilter] = useState('')

  const [modalOpen, setModalOpen] = useState(false)
  const [selected, setSelected] = useState(null)
  const [permModalOpen, setPermModalOpen] = useState(false)
  const { permissions } = useAuth()

  useEffect(() => {
    loadFuncionarios()
  }, [currentPage, perPage, ativoFilter])

  useEffect(() => {
    if (searchTimeout) clearTimeout(searchTimeout)
    const timeout = setTimeout(() => {
      setCurrentPage(1)
      loadFuncionarios()
    }, 500)
    setSearchTimeout(timeout)
    return () => { if (timeout) clearTimeout(timeout) }
  }, [search])

  const loadFuncionarios = async () => {
    try {
      setLoading(true)
      const params = {
        page: currentPage,
        per_page: perPage,
        ...(search && { search }),
        ...(ativoFilter && { ativo: ativoFilter })
      }
      const response = await api.get('/funcionarios/list.php', params)
      if (response.ok) {
        setFuncionarios(response.data || [])
        setTotal(response.total || 0)
        setError(null)
      } else {
        setError(response.status === 401 ? 'Sessão expirada. Faça login novamente.' : (response.message || 'Erro ao carregar funcionários'))
        setFuncionarios([])
        setTotal(0)
      }
    } catch (err) {
      setError('Erro de conexão')
      setFuncionarios([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => { setSelected(null); setModalOpen(true) }
  const handleEdit = (row) => { setSelected(row); setModalOpen(true) }
  const handlePerm = (row) => { setSelected(row); setPermModalOpen(true) }
  const handleDelete = async (id) => {
    try {
      if (!window.confirm('Tem certeza que deseja excluir este funcionário?')) return
      const response = await api.delete(`/funcionarios/delete.php?id=${id}`)
      if (response.ok) {
        loadFuncionarios()
      } else {
        throw new Error(response.message || 'Erro ao excluir funcionário')
      }
    } catch (error) {
      alert(`Erro: ${error.message}`)
      throw error
    }
  }

  const handleSave = async (data) => {
    if (selected) {
      const response = await api.put(`/funcionarios/update.php?id=${selected.id_funcionario}`, data)
      if (response.ok) loadFuncionarios(); else throw new Error(response.message || 'Erro ao atualizar funcionário')
    } else {
      const response = await api.post('/funcionarios/create.php', data)
      if (response.ok) loadFuncionarios(); else throw new Error(response.message || 'Erro ao criar funcionário')
    }
  }

  const totalPages = Math.ceil(total / perPage)
  const startItem = (currentPage - 1) * perPage + 1
  const endItem = Math.min(currentPage * perPage, total)

  return (
    <div className="page-transition">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Funcionários</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Gerencie o cadastro de funcionários do sistema</p>
          </div>
          {(permissions?.editar_funcionario === 'sim') && (
            <button onClick={handleCreate} className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Novo Funcionário
            </button>
          )}
        </div>
      </div>

      <div className="card p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input type="text" placeholder="Buscar por nome, email, telefone..." value={search} onChange={(e) => setSearch(e.target.value)} className="input pl-10 w-full" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Ativo:</label>
            <select value={ativoFilter} onChange={(e) => setAtivoFilter(e.target.value)} className="input w-28">
              <option value="">Todos</option>
              <option value="sim">Sim</option>
              <option value="nao">Não</option>
            </select>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Por página:</label>
            <select value={perPage} onChange={(e) => { setPerPage(parseInt(e.target.value)); setCurrentPage(1) }} className="input w-20">
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-red-500 mb-4"><Users className="h-12 w-12" /></div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Erro ao carregar dados</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{error}</p>
            <button onClick={loadFuncionarios} className="btn-primary">Tentar novamente</button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nome</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Telefone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cargo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ativo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {funcionarios.length > 0 ? (
                    funcionarios.map((f) => (
                      <tr key={f.id_funcionario} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{f.id_funcionario}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{f.nome}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{f.email || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{f.telefone || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{f.cargo || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{f.ativo || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center space-x-2">
                            {(permissions?.editar_funcionario === 'sim') && (
                              <button onClick={() => handleEdit(f)} className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300" title="Editar">
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                            <button onClick={() => handlePerm(f)} className="text-emerald-600 hover:text-emerald-900 dark:text-emerald-400 dark:hover:text-emerald-300" title="Permissões">
                              <Shield className="h-4 w-4" />
                            </button>
                            {(permissions?.editar_funcionario === 'sim') && (
                              <button onClick={() => handleDelete(f.id_funcionario)} className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300" title="Excluir">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center">
                        <div className="text-gray-500 dark:text-gray-400">
                          <Users className="mx-auto h-12 w-12 mb-4" />
                          <p className="text-lg font-medium">Nenhum funcionário encontrado</p>
                          <p className="text-sm">{search || ativoFilter ? 'Tente ajustar os filtros de busca' : 'Não há funcionários cadastrados'}</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {total > 0 && (
              <div className="bg-white dark:bg-gray-900 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Mostrando <span className="font-medium">{startItem}</span> a <span className="font-medium">{endItem}</span> de <span className="font-medium">{total}</span> resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        (page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)) ? (
                          <button key={page} onClick={() => setCurrentPage(page)} className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === currentPage ? 'z-10 bg-primary-50 border-primary-500 text-primary-600 dark:bg-primary-900/20 dark:border-primary-400 dark:text-primary-400' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>{page}</button>
                        ) : (page === currentPage - 3 || page === currentPage + 3) ? (
                          <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300">...</span>
                        ) : null
                      ))}
                      <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <FuncionarioModal isOpen={modalOpen} onClose={() => setModalOpen(false)} funcionario={selected} onSave={handleSave} onDelete={handleDelete} canEdit={permissions?.editar_funcionario === 'sim'} />
      <PermissoesModal isOpen={permModalOpen} onClose={() => setPermModalOpen(false)} funcionario={selected} />
    </div>
  )
}

export default FuncionariosList